// src/utils/muteNudge.ts
//
// Lightweight, one-time “enable sound” nudge that points at the mute button.
// - Shown only once per session (uses sessionStorage key)
// - Adds a brief pulse animation to the mute button
// - Renders a small tip bubble next to the button (fixed-position overlay)
// - Auto-cleans after 3s or on first click/keyboard action
//
// Usage:
//   import { nudgeMuteButton } from "@/utils/muteNudge";
//   nudgeMuteButton(section); // optionally pass custom message
// Requires CSS classes (.sc-nudge-tip, orientation modifiers, and .sc-nudge-pulse) to be present in layout.css
//
// Notes:
// - No DOM restructuring (does not wrap the button), so it’s CSS-safe
// - The bubble is positioned relative to viewport near the button
// - If no button is found, it quietly no-ops

const SESSION_KEY = "sc:mute-nudge-done"; // once per tab/session

function oncePerSession(): boolean {
  try {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return false;
    sessionStorage.setItem(SESSION_KEY, "1");
    return true;
  } catch {
    // If storage is blocked, still try to show once
    return true;
  }
}

function findMuteButton(section: HTMLElement): HTMLElement | null {
  return section.querySelector<HTMLElement>(
    ".video-mute-button, .mute-button, .video-reel-mute-button, .hud-btn.mute-button, .narration-mute-button"
  );
}

/**
 * Show a short-lived nudge near the mute button.
 * @param section Section element containing the HUD
 * @param msg Optional tip text (default: "Tap to enable sound")
 */
export function nudgeMuteButton(section: HTMLElement, msg = "Tap to enable sound") {
  // throttle to once per session/tab
  if (!oncePerSession()) return;

  const btn = findMuteButton(section);
  if (!btn) return;

  // Add a quick pulse to the button
  btn.classList.add("sc-nudge-pulse");

  // Create tip bubble
  const tip = document.createElement("div");
  tip.className = "sc-nudge-tip";
  tip.textContent = msg;
  document.body.appendChild(tip);

  // Position the bubble near the button (above-right)
  const positionTip = () => {
    const r = btn.getBoundingClientRect();
    const gap = 8;

    // Remove previous orientation classes
    tip.classList.remove("sc-nudge-tip--left","sc-nudge-tip--right","sc-nudge-tip--above","sc-nudge-tip--below");

    // Measure after in DOM to get correct size
    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceLeft = r.left - 8;
    const spaceRight = vw - r.right - 8;
    const spaceAbove = r.top - 8;
    const spaceBelow = vh - r.bottom - 8;

    // Prefer placing the tip to the LEFT of the button (HUD is on the right)
    if (spaceLeft >= tipWidth + gap) {
      const top = Math.max(8, Math.min(vh - tipHeight - 8, r.top + (r.height - tipHeight) / 2));
      const left = r.left - tipWidth - gap;
      tip.style.top = `${top}px`;
      tip.style.left = `${left}px`;
      tip.style.transform = "none";
      tip.classList.add("sc-nudge-tip--left");
      return;
    }

    // Otherwise, try placing to the RIGHT
    if (spaceRight >= tipWidth + gap) {
      const top = Math.max(8, Math.min(vh - tipHeight - 8, r.top + (r.height - tipHeight) / 2));
      const left = r.right + gap;
      tip.style.top = `${top}px`;
      tip.style.left = `${left}px`;
      tip.style.transform = "none";
      tip.classList.add("sc-nudge-tip--right");
      return;
    }

    // Fallback ABOVE the button
    if (spaceAbove >= tipHeight + gap) {
      const top = Math.max(8, r.top - gap);
      const centerX = r.left + r.width / 2 - tipWidth / 2;
      const left = Math.max(8, Math.min(vw - tipWidth - 8, centerX));
      tip.style.top = `${top}px`;
      tip.style.left = `${left}px`;
      tip.style.transform = "translateY(-100%)";
      tip.classList.add("sc-nudge-tip--above");
      return;
    }

    // Last resort BELOW the button
    const top = Math.min(vh - 8, r.bottom + gap);
    const centerX = r.left + r.width / 2 - tipWidth / 2;
    const left = Math.max(8, Math.min(vw - tipWidth - 8, centerX));
    tip.style.top = `${top}px`;
    tip.style.left = `${left}px`;
    tip.style.transform = "none";
    tip.classList.add("sc-nudge-tip--below");
  };

  positionTip();

  // Reposition on layout changes, then remove listeners on cleanup
  const reposition = () => positionTip();
  window.addEventListener("resize", reposition, true);
  window.addEventListener("scroll", reposition, true);

  // Cleanup on first user interaction or after timeout
  const cleanup = () => {
    btn.classList.remove("sc-nudge-pulse");
    tip.remove();
    window.removeEventListener("resize", reposition, true);
    window.removeEventListener("scroll", reposition, true);
    btn.removeEventListener("click", onClick, true);
    document.removeEventListener("keydown", onKey, true);
  };
  const onClick = () => cleanup();
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.code === "Space") cleanup();
  };

  btn.addEventListener("click", onClick, { capture: true, once: true } as any);
  document.addEventListener("keydown", onKey, true);

  // Auto-remove after 3 seconds
  window.setTimeout(cleanup, 3000);
}